import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Send } from 'lucide-react';
import { api } from '@/lib/api';
import type { ChatMessage, Organization } from '@/lib/types';
import type { OrgMember } from '@/lib/constants';
import { useAuth } from '@/lib/auth';
import { PageHeader } from '@/components/layout/PageHeader';
import { cn } from '@/lib/utils';
import { Button, Input, Spinner } from '@/components/ui';

/**
 * Mesajın altındaki tarih ve saat.
 *
 * Bugünkü mesajlarda yalnız saat yazıyor — sohbetin çoğu aynı gün
 * geçiyor, her satıra tarih koymak gürültü olurdu. Eski mesajlarda
 * gün ve ay da görünüyor, yıl ancak başka bir yıldansa.
 */
function zamanYaz(ts: string): string {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  const simdi = new Date();
  const ayniGun =
    d.getDate() === simdi.getDate() &&
    d.getMonth() === simdi.getMonth() &&
    d.getFullYear() === simdi.getFullYear();
  const saat = d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
  if (ayniGun) return saat;
  const gun = d.toLocaleDateString('tr-TR', {
    day: 'numeric',
    month: 'short',
    ...(d.getFullYear() === simdi.getFullYear() ? {} : { year: 'numeric' }),
  });
  return `${gun} ${saat}`;
}

const kucult = (s: string) => s.toLocaleLowerCase('tr');
const kacir = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Yazılmakta olan anma: imlecin solundaki `@` ve sonrasındaki harfler.
 *
 * Adlar iki kelimeli olduğu için bir boşluğa izin veriliyor; ikinciden
 * sonrası artık cümledir, liste kapanıyor.
 */
function anmaSorgusu(metin: string, imlec: number): { bas: number; sorgu: string } | null {
  const once = metin.slice(0, imlec);
  const bas = once.lastIndexOf('@');
  if (bas < 0) return null;
  if (bas > 0 && !/\s/.test(once[bas - 1])) return null;
  const kuyruk = once.slice(bas + 1);
  if (kuyruk.length > 24 || /[\n@]/.test(kuyruk)) return null;
  if ((kuyruk.match(/ /g)?.length ?? 0) > 1) return null;
  return { bas, sorgu: kuyruk };
}

/** Mesaj metnindeki anmaları işaretler; kendi adımız daha belirgin. */
function AnmaliMetin({
  metin,
  adlar,
  kendiAdi,
  kendiMesaji,
}: {
  metin?: string;
  adlar: string[];
  kendiAdi?: string;
  kendiMesaji: boolean;
}) {
  const parcalar = useMemo(() => {
    if (!metin) return [];
    if (!adlar.length) return [{ anma: false, s: metin }];
    const kalip = new RegExp(
      `@(${[...adlar].sort((a, b) => b.length - a.length).map(kacir).join('|')})`,
      'gi',
    );
    const cikti: { anma: boolean; s: string }[] = [];
    let son = 0;
    for (const e of metin.matchAll(kalip)) {
      const yer = e.index ?? 0;
      if (yer > son) cikti.push({ anma: false, s: metin.slice(son, yer) });
      cikti.push({ anma: true, s: e[0] });
      son = yer + e[0].length;
    }
    if (son < metin.length) cikti.push({ anma: false, s: metin.slice(son) });
    return cikti;
  }, [metin, adlar]);

  return (
    <p>
      {parcalar.map((p, i) =>
        p.anma ? (
          <span
            key={i}
            className={cn(
              'rounded px-1 font-medium',
              kendiMesaji ? 'bg-white/20' : 'bg-accent/20 text-primary',
              !kendiMesaji && kendiAdi && kucult(p.s) === `@${kucult(kendiAdi)}` && 'bg-accent/45',
            )}
          >
            {p.s}
          </span>
        ) : (
          <span key={i}>{p.s}</span>
        ),
      )}
    </p>
  );
}

export function ChatPage() {
  const { user } = useAuth();
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sending, setSending] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const girdiRef = useRef<HTMLInputElement>(null);
  const [imlec, setImlec] = useState(0);
  const [vurgu, setVurgu] = useState(0);
  const [anmaKapali, setAnmaKapali] = useState(false);

  const { data: orgs } = useQuery({
    queryKey: ['organizations'],
    queryFn: () => api<{ organizations: Organization[] }>('/organizations'),
  });

  const orgId = orgs?.organizations[0]?.id;

  /* Anma listesi için katılımcılar. Katılımcı sayfası da aynı sorguyu
     kullanıyor, önbellekten geliyor. */
  const { data: membersData } = useQuery({
    queryKey: ['members', orgId],
    queryFn: () => api<{ members: OrgMember[] }>(`/organizations/${orgId}/members`),
    enabled: !!orgId,
  });

  const katilimcilar = useMemo(
    () =>
      (membersData?.members ?? [])
        .filter((m) => !m.status || m.status === 'approved')
        .map((m) => ({ id: m.userId, ad: m.fullName ?? '' }))
        .filter((k) => k.ad.trim().length > 0),
    [membersData],
  );
  const adlar = useMemo(() => katilimcilar.map((k) => k.ad), [katilimcilar]);

  /* Oda listesi ilk odanın mesajlarını da getiriyor; böylece sohbet
     açılırken üç değil iki istek yapılıyor. */
  const { data: rooms, isLoading } = useQuery({
    queryKey: ['chat-rooms', orgId],
    queryFn: () =>
      api<{ rooms: { id: string; name: string }[]; messages?: ChatMessage[] }>(
        `/chat/org/${orgId}/rooms`,
      ),
    enabled: !!orgId,
  });

  const roomId = rooms?.rooms[0]?.id;
  const ilkMesajlar = rooms?.messages;

  /* Liste ile gelen mesajlar hemen ekrana; ayrıca beklemeye gerek yok. */
  useEffect(() => {
    if (ilkMesajlar) setMessages(ilkMesajlar);
  }, [ilkMesajlar]);

  useEffect(() => {
    if (!roomId) return;

    async function loadMessages() {
      const data = await api<{ messages: ChatMessage[] }>(`/chat/rooms/${roomId}/messages`);
      setMessages(data.messages ?? []);
    }

    /* İlk yükleme oda listesiyle geldiyse hemen bir daha sormuyoruz. */
    if (!ilkMesajlar) loadMessages();

    /* Sekme arkadayken beş saniyede bir sormanın anlamı yok: her
       sorgu Durable Object'i uyandırıyor, telefonda da boşuna pil
       yakıyor. Sekme öne gelince hemen bir kere soruluyor. */
    const sor = () => {
      if (document.visibilityState === 'visible') loadMessages();
    };
    const interval = setInterval(sor, 5000);
    document.addEventListener('visibilitychange', sor);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', sor);
    };
  }, [roomId, ilkMesajlar]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const anma = anmaKapali ? null : anmaSorgusu(message, imlec);
  const oneriler = useMemo(() => {
    if (!anma) return [];
    const s = kucult(anma.sorgu.trim());
    const uygun = s
      ? katilimcilar.filter((k) => {
          const ad = kucult(k.ad);
          return ad.startsWith(s) || ad.split(/\s+/).some((p) => p.startsWith(s)) || ad.includes(s);
        })
      : katilimcilar;
    return uygun.slice(0, 6);
  }, [anma, katilimcilar]);

  useEffect(() => setVurgu(0), [anma?.sorgu]);

  function anmaEkle(ad: string) {
    const a = anmaSorgusu(message, imlec);
    if (!a) return;
    const yeni = `${message.slice(0, a.bas)}@${ad} ${message.slice(imlec)}`;
    const yeniImlec = a.bas + ad.length + 2;
    setMessage(yeni);
    setImlec(yeniImlec);
    requestAnimationFrame(() => {
      girdiRef.current?.focus();
      girdiRef.current?.setSelectionRange(yeniImlec, yeniImlec);
    });
  }

  function tusla(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!oneriler.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setVurgu((v) => (v + 1) % oneriler.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setVurgu((v) => (v - 1 + oneriler.length) % oneriler.length);
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      /* Liste açıkken Enter mesajı göndermiyor, adı seçiyor. */
      e.preventDefault();
      anmaEkle(oneriler[vurgu].ad);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setAnmaKapali(true);
    }
  }

  async function sendMessage(e: React.FormEvent) {
    e.preventDefault();
    if (!message.trim() || !roomId) return;

    setSending(true);
    try {
      const data = await api<{ message: ChatMessage }>(`/chat/rooms/${roomId}/messages`, {
        method: 'POST',
        body: JSON.stringify({ content: message.trim() }),
      });
      setMessages((prev) => [...prev, data.message]);
      setMessage('');
      setImlec(0);
    } finally {
      setSending(false);
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-col">
      <PageHeader title={rooms?.rooms[0]?.name ?? 'Sohbet'} subtitle="Organizasyon genel sohbeti" />

      <div className="ornate-card mb-32 flex-1 overflow-y-auto rounded-xl p-4">
        <div className="space-y-3">
          {messages.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              Henüz mesaj yok. İlk mesajı siz gönderin.
            </p>
          ) : (
            messages.map((msg) => {
              const isOwn = msg.userId === user?.id;
              return (
                <div key={msg.id} className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${
                      isOwn
                        ? 'bg-primary text-primary-foreground'
                        : 'border border-border bg-white text-primary'
                    }`}
                  >
                    {/* Ad her mesajda yazıyor, kendi mesajlarımızda da:
                        aynı hesaba birden çok kişi bakabiliyor. */}
                    <p
                      className={`mb-1 text-xs font-medium ${
                        isOwn ? 'text-primary-foreground/80' : 'text-accent'
                      }`}
                    >
                      {msg.userName}
                    </p>
                    <AnmaliMetin
                      metin={msg.content}
                      adlar={adlar}
                      kendiAdi={user?.fullName}
                      kendiMesaji={isOwn}
                    />
                    <p
                      className={`mt-1 text-[10px] ${
                        isOwn ? 'text-primary-foreground/65' : 'text-muted-foreground'
                      }`}
                    >
                      {zamanYaz(msg.createdAt)}
                    </p>
                  </div>
                </div>
              );
            })
          )}
          <div ref={bottomRef} />
        </div>
      </div>

      <form
        onSubmit={sendMessage}
        className="chat-input-bar fixed left-0 right-0 z-30 mx-auto flex max-w-lg gap-2 border-t-2 border-accent/30 bg-parchment px-4 py-3 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]"
      >
        {/* @ yazılınca katılımcı listesi yazma çubuğunun üstünde açılıyor. */}
        {oneriler.length > 0 && (
          <div className="absolute bottom-full left-4 right-4 mb-2 overflow-hidden rounded-xl border border-border bg-white shadow-lg">
            <p className="border-b border-border px-3 py-1.5 text-[11px] uppercase tracking-wider text-muted-foreground">
              Katılımcılar
            </p>
            <ul className="max-h-56 overflow-y-auto">
              {oneriler.map((k, i) => (
                <li key={k.id}>
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => anmaEkle(k.ad)}
                    className={cn(
                      'block w-full px-3 py-2 text-left text-sm text-primary',
                      i === vurgu ? 'bg-accent/20' : 'hover:bg-accent/10',
                    )}
                  >
                    {k.ad}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
        <Input
          ref={girdiRef}
          placeholder="Mesaj yazın... (@ ile kişi anın)"
          value={message}
          onChange={(e) => {
            setMessage(e.target.value);
            setImlec(e.target.selectionStart ?? e.target.value.length);
            setAnmaKapali(false);
          }}
          onKeyDown={tusla}
          onKeyUp={(e) => setImlec(e.currentTarget.selectionStart ?? 0)}
          onClick={(e) => setImlec(e.currentTarget.selectionStart ?? 0)}
          onBlur={() => setAnmaKapali(true)}
          onFocus={() => setAnmaKapali(false)}
          disabled={!roomId}
          className="flex-1"
        />
        <Button type="submit" size="icon" variant="gold" disabled={sending || !roomId}>
          {sending ? <Spinner /> : <Send className="h-4 w-4" />}
        </Button>
      </form>
    </div>
  );
}
